import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  DialogContentText,
  Snackbar,
  Alert,
  Box,
  Divider
} from "@mui/material";
import { useCancelSubscriptionMutation } from "../../store/userApi";

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: (wasCancelled?: boolean) => void;
}

export const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  open,
  onClose
}) => {
  const [cancelSubscription, { isLoading }] = useCancelSubscriptionMutation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleConfirmCancel = async () => {
    try {
      await cancelSubscription().unwrap();
      setShowSuccess(true);
      setTimeout(() => {
        onClose(true);
      }, 2000);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to cancel subscription');
      setShowError(true);
    }
  };
  
  const handleClose = () => {
    onClose(false);
  };
  
  const handleSuccessClose = () => {
    setShowSuccess(false);
  };
  
  const handleErrorClose = () => {
    setShowError(false);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1f2e",
            color: "#ffffff"
          }
        }}
      >
        <DialogTitle>Cancel Your Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}>
            Are you sure you want to cancel your subscription?
          </DialogContentText>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              What happens when you cancel:
            </Typography>
            <Divider sx={{ my: 1, bgcolor: "rgba(255,255,255,0.1)" }} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" paragraph sx={{ display: "flex", alignItems: "center" }}>
                • You'll have access to Pro features until the end of your current billing period
              </Typography>
              <Typography variant="body1" paragraph sx={{ display: "flex", alignItems: "center" }}>
                • After your subscription ends, you'll be downgraded to the Free plan
              </Typography>
              <Typography variant="body1" paragraph sx={{ display: "flex", alignItems: "center" }}>
                • Free plan limits you to {process.env.REACT_APP_FREE_TRANSCRIPTION_LIMIT || '5'} transcriptions
              </Typography>
              <Typography variant="body1" paragraph sx={{ display: "flex", alignItems: "center" }}>
                • You can resubscribe at any time
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body2" color="error">
            This action cannot be undone automatically. To restore Pro features, you'll need to subscribe again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            Keep Subscription
          </Button>
          <Button 
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm Cancellation"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={showSuccess} autoHideDuration={6000} onClose={handleSuccessClose}>
        <Alert onClose={handleSuccessClose} severity="success">
          Your subscription has been canceled. You'll have access until the end of your current billing period.
        </Alert>
      </Snackbar>
      
      <Snackbar open={showError} autoHideDuration={6000} onClose={handleErrorClose}>
        <Alert onClose={handleErrorClose} severity="error">
          {errorMessage || 'Failed to cancel subscription'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CancelSubscriptionDialog; 